/**
 * Icons, drawn inline.
 *
 * A telematics console needs about twenty glyphs. Shipping an icon package
 * for that costs more bytes than the rest of the UI chrome put together, and
 * inline paths can take the accent colour without a wrapper.
 *
 * All are 24×24 on a 1.6 stroke, so they sit on the same optical weight as
 * the condensed labels beside them.
 */

export type IconName =
  | 'gauge'
  | 'map'
  | 'bell'
  | 'chart'
  | 'truck'
  | 'user'
  | 'users'
  | 'polygon'
  | 'chip'
  | 'building'
  | 'receipt'
  | 'palette'
  | 'shield'
  | 'pulse'
  | 'contrast'
  | 'search'
  | 'plus'
  | 'download'
  | 'play'
  | 'pause'
  | 'crosshair'
  | 'route'
  | 'clock'
  | 'fuel'
  | 'signal'
  | 'lock'
  | 'trash'
  | 'edit'
  | 'chevron'
  | 'external'
  | 'sun'
  | 'moon'
  | 'monitor'

const PATHS: Record<IconName, string> = {
  gauge: 'M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm1.5-2.5L18 7M3.5 18a9.5 9.5 0 1 1 17 0',
  map: 'M9 3 3 5.5v15L9 18m0-15 6 3m-6-3v15m6-12 6-2.5v15L15 18m0-15v15m0 0-6-3',
  bell: 'M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6M10.5 20a2 2 0 0 0 3 0',
  chart: 'M3 20h18M6 16V9m5 7V5m5 11v-5',
  truck: 'M2 6h11v10H2zM13 9h4l3 3.5V16h-7M6 19.5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm11 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z',
  user: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-8 8a8 8 0 0 1 16 0',
  users: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm-7 9a7 7 0 0 1 14 0m1-15.7a3.5 3.5 0 0 1 0 6.9m2.5 8.8a7 7 0 0 0-3-5.7',
  polygon: 'M12 3 21 9.5 17.5 20h-11L3 9.5 12 3Z',
  chip: 'M7 7h10v10H7zM9.5 3v4M14.5 3v4M9.5 17v4M14.5 17v4M3 9.5h4M3 14.5h4M17 9.5h4M17 14.5h4',
  building: 'M4 21V6l7-3 7 3v15M4 21h16M9 21v-4h6v4M8 9h1m3 0h1m3 0h1M8 13h1m3 0h1m3 0h1',
  receipt: 'M5 3v18l2.5-1.5L10 21l2-1.5L14 21l2.5-1.5L19 21V3zM8.5 8h7M8.5 12h7M8.5 16h4',
  palette: 'M12 3a9 9 0 0 0 0 18c1.5 0 2-1 2-2s-1-1.5-1-2.5 1-1.5 2-1.5h1.5A3.5 3.5 0 0 0 21 11.5C21 6.8 16.9 3 12 3Zm-4.5 8.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm3-3.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2Zm5 0a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z',
  shield: 'M12 3 4.5 6v6c0 4.5 3.2 7.8 7.5 9 4.3-1.2 7.5-4.5 7.5-9V6L12 3Z',
  pulse: 'M2 12h4l2.5-7 4 14L15.5 12H22',
  contrast: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 0v18',
  search: 'M10.5 17a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13Zm5-1.5L20 20',
  plus: 'M12 5v14M5 12h14',
  download: 'M12 3v12m0 0 4.5-4.5M12 15l-4.5-4.5M4 19h16',
  play: 'M7 4.5v15l13-7.5-13-7.5Z',
  pause: 'M8 5v14M16 5v14',
  crosshair: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18ZM12 1.5v4M12 18.5v4M1.5 12h4M18.5 12h4',
  route: 'M6.5 8.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Zm11 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM6.5 8.5V13a5 5 0 0 0 5 5h1.5',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 4.5V12l3.5 2',
  fuel: 'M4 21V5a2 2 0 0 1 2-2h5a2 2 0 0 1 2 2v16M3 21h12M6 8h5m4 1.5 2.5 2.5V18a1.5 1.5 0 0 0 3 0V9l-3-3',
  signal: 'M4 20v-4m5 4V11m5 9V6.5m5 13.5V3',
  lock: 'M7 11V8a5 5 0 0 1 10 0v3M5.5 11h13v9.5h-13z',
  trash: 'M4 7h16M9.5 7V4.5h5V7M6.5 7l1 13.5h9L17.5 7M10 11v6M14 11v6',
  edit: 'M4 20h4L19 9a2.5 2.5 0 0 0-3.5-3.5L4.5 16.5V20ZM14 7l3.5 3.5',
  chevron: 'm9 6 6 6-6 6',
  external: 'M14 4h6v6M20 4l-8.5 8.5M18 14v5.5H4.5V6H10',
  sun: 'M12 16.5a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9ZM12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4m11.4-11.4 1.4-1.4',
  moon: 'M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5a8.5 8.5 0 1 0 10.7 10.7Z',
  monitor: 'M3 4.5h18v11.5H3zM8 20h8M12 16v4',
}

export function Icon({
  name,
  size = 16,
  className = '',
  strokeWidth = 1.6,
}: {
  name: IconName
  size?: number
  className?: string
  strokeWidth?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`shrink-0 ${className}`}
      aria-hidden
    >
      <path d={PATHS[name]} />
    </svg>
  )
}
